/**
 * ReportBugPanel — bug/feature feedback form, driven by lifted state from HelpLauncher.
 * Mirrors FeedbackButton's form verbatim; all state is owned by the parent.
 */

import { createFeedback } from '../../api/feedback';

const PRIMARY = 'var(--primary)';
const GREEN = 'var(--success)';
const RED = 'var(--danger)';

export interface ReportBugState {
  type: 'bug' | 'feature';
  title: string;
  description: string;
  result: { ok: boolean; message: string } | null;
}

interface ReportBugPanelProps {
  pageUrl: string;
  state: ReportBugState;
  onChange: (patch: Partial<ReportBugState>) => void;
  submitting: boolean;
  onSubmittingChange: (v: boolean) => void;
  onSubmitted: () => void;
}

export default function ReportBugPanel({
  pageUrl,
  state,
  onChange,
  submitting,
  onSubmittingChange,
  onSubmitted,
}: ReportBugPanelProps) {
  const { type, title, description, result } = state;
  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    onSubmittingChange(true);
    onChange({ result: null });
    try {
      const response = await createFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        page_url: pageUrl,
      });
      onChange({
        result: {
          ok: true,
          message: response?.github_delivery_status === 'pending'
            ? 'Thank you. Your feedback was saved for follow-up.'
            : 'Thank you for your feedback!',
        },
      });
      onSubmitted();
    } catch {
      onChange({ result: { ok: false, message: 'Failed to submit feedback. Please try again.' } });
    } finally {
      onSubmittingChange(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Result banner */}
      {result && (
        <div
          style={{
            background: result.ok ? 'rgba(52, 168, 83, 0.15)' : 'rgba(239, 68, 68, 0.1)',
            color: result.ok ? GREEN : RED,
            border: `1px solid ${result.ok ? 'rgba(95, 191, 150, 0.34)' : 'rgba(221, 122, 115, 0.34)'}`,
            borderRadius: 4,
            padding: '8px 12px',
            fontSize: 14,
            fontWeight: result.ok ? 600 : 400,
          }}
        >
          {result.message}
        </div>
      )}

      {/* Type selector */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onChange({ type: 'bug' })}
          style={{
            flex: 1,
            padding: '7px 0',
            borderRadius: 4,
            border: `1.5px solid ${type === 'bug' ? PRIMARY : 'var(--border)'}`,
            background: type === 'bug' ? PRIMARY : 'var(--surface-strong)',
            color: type === 'bug' ? 'var(--text-inverse)' : 'var(--text-secondary)',
            fontWeight: type === 'bug' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Bug Report
        </button>
        <button
          onClick={() => onChange({ type: 'feature' })}
          style={{
            flex: 1,
            padding: '7px 0',
            borderRadius: 4,
            border: `1.5px solid ${type === 'feature' ? PRIMARY : 'var(--border)'}`,
            background: type === 'feature' ? PRIMARY : 'var(--surface-strong)',
            color: type === 'feature' ? 'var(--text-inverse)' : 'var(--text-secondary)',
            fontWeight: type === 'feature' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Feature Request
        </button>
      </div>

      {/* Title */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Brief summary"
          style={{
            padding: '7px 10px',
            borderRadius: 4,
            border: '1px solid var(--border)',
            fontSize: 14,
            background: 'var(--surface-strong)',
            color: 'var(--text-primary)',
          }}
        />
      </div>

      {/* Description */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Description</label>
        <textarea
          rows={4}
          value={description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Describe the issue or feature in detail"
          style={{
            padding: '7px 10px',
            borderRadius: 4,
            border: '1px solid var(--border)',
            fontSize: 14,
            resize: 'vertical',
            fontFamily: 'inherit',
            background: 'var(--surface-strong)',
            color: 'var(--text-primary)',
          }}
        />
      </div>

      {/* Page context */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Page</label>
        <input
          type="text"
          value={pageUrl}
          readOnly
          style={{
            padding: '7px 10px',
            borderRadius: 4,
            border: '1px solid var(--border)',
            fontSize: 13,
            color: 'var(--text-muted)',
            background: 'var(--bg-subtle)',
          }}
        />
      </div>

      {/* Submit */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '7px 18px',
            borderRadius: 4,
            border: 'none',
            background: canSubmit ? PRIMARY : 'var(--disabled-bg)',
            color: 'var(--text-inverse)',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
